/**
 * Organizations admin handler
 * Manages organizations within accounts
 */

import { useState, useCallback, useEffect } from "react";
import type { accountTypes } from "@pineseed/spec";
import { useBaseEntityAdmin, type EntityAdminHandler } from "../useEntityAdmin";
import type { UseAdminAppReturn } from "../useAdminApp";

type Organization = accountTypes["Organization"];

export interface OrganizationsAdminHandler extends EntityAdminHandler<Organization> {
  // Organization-specific operations
  getOrganizationsByAccount: (accountId: string) => Promise<void>;
  getStats: () => {
    total: number;
    byAccount: Record<string, number>;
  };
}

export function useOrganizationsAdmin(adminApp: UseAdminAppReturn): OrganizationsAdminHandler {
  // Base CRUD operations - using 'accounts' as list endpoint since we need account context
  const baseHandler = useBaseEntityAdmin<Organization>({
    adminApp,
    endpoint: 'accounts', // Will be overridden by custom loadEntities
    entityName: 'organization',
    crudEndpoint: 'organization',
    workspaceScoped: false,
  });

  const [currentAccountId, setCurrentAccountId] = useState<string | null>(null);

  // Override loadEntities to load all organizations or by account
  const loadEntities = useCallback(async (params?: Record<string, string>) => {
    baseHandler.setLoading(true);
    baseHandler.setError(null);
    
    try {
      // For now, we'll need to fetch from all accounts
      // In a real implementation, you might have a /organizations endpoint
      const accountsResponse = await fetch(`${adminApp.httpUrl}/api/accounts`, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      
      if (!accountsResponse.ok) {
        throw new Error('Failed to fetch accounts');
      }
      
      const accounts = await accountsResponse.json();
      const allOrgs: Organization[] = [];
      
      // Fetch organizations for each account
      for (const account of accounts) {
        const orgsResponse = await fetch(
          `${adminApp.httpUrl}/api/account/${account.id}/organizations`,
          {
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          }
        );
        
        if (orgsResponse.ok) {
          const orgs = await orgsResponse.json();
          allOrgs.push(...orgs);
        }
      }
      
      baseHandler.setEntities(allOrgs);
      console.log(`Loaded ${allOrgs.length} organization(s)`);
    } catch (err) {
      const errorMsg = `Failed to load organizations: ${err instanceof Error ? err.message : 'Unknown error'}`;
      baseHandler.setError(errorMsg);
      console.error(errorMsg);
      baseHandler.setEntities([]);
    } finally {
      baseHandler.setLoading(false);
    }
  }, [adminApp, baseHandler]);

  // Get organizations for a specific account
  const getOrganizationsByAccount = useCallback(async (accountId: string) => {
    baseHandler.setLoading(true);
    baseHandler.setError(null);
    setCurrentAccountId(accountId);
    
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/account/${accountId}/organizations`,
        {
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );
      
      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        console.error(`❌ API Error [GET /account/${accountId}/organizations]:`, {
          status: response.status,
          statusText: response.statusText,
          body: errorBody,
        });
        throw new Error('Failed to fetch organizations');
      }
      
      const orgs = await response.json();
      baseHandler.setEntities(orgs);
      console.log(`✅ Loaded ${orgs.length} organization(s) for account ${accountId}`);
    } catch (err) {
      const errorMsg = `Failed to load organizations: ${err instanceof Error ? err.message : 'Unknown error'}`;
      baseHandler.setError(errorMsg);
      console.error(errorMsg);
      baseHandler.setEntities([]);
    } finally {
      baseHandler.setLoading(false);
    }
  }, [adminApp, baseHandler]);

  // Get statistics
  const getStats = useCallback(() => {
    const byAccount = baseHandler.entities.reduce((acc, org) => {
      acc[org.accountId] = (acc[org.accountId] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total: baseHandler.entities.length,
      byAccount,
    };
  }, [baseHandler.entities]);

  // Load all organizations on mount
  useEffect(() => {
    loadEntities();
  }, []); // Only on mount

  return {
    ...baseHandler,
    loadEntities,
    getOrganizationsByAccount,
    getStats,
  };
}

